/**
 * The Task List's "Sort: By hand". Each task keeps a number (`listOrder`, on the server) for where the student dragged
 * it - one order across every grouping, so a task keeps its place next to its neighbours whichever way the list is
 * grouped. Tasks never moved have none and come after the numbered ones, in the list's usual order: until the first
 * drag the list looks as it does sorted by due date, and new tasks land at the end of their group.
 */
export type HasListOrder = { listOrder?: number | null };

export const byListOrder = (a: HasListOrder, b: HasListOrder) => {
  const first = a.listOrder ?? null;
  const second = b.listOrder ?? null;
  if (first === null) return second === null ? 0 : 1;
  if (second === null) return -1;
  return first - second;
};

/** The tasks in the student's own order; those never moved keep their order after the rest (the sort is stable). */
// (Any task-like objects: the shared WorkloadTask type has no listOrder yet - it comes from this branch's backend.)
export const sortByHand = <T>(tasks: T[]): T[] =>
  [...tasks].sort((a, b) => byListOrder(a as HasListOrder, b as HasListOrder));

/**
 * Where a dragged task lands among the tasks of its group as shown (`ids`, top to bottom, the dragged one included):
 * the tasks just above and below the slot it's dropped into, as the server takes them. `slot` counts the gaps from
 * the top (0 = above the first). Null when the drop leaves it where it was.
 */
export const dropNeighbours = (
  ids: number[],
  draggedId: number,
  slot: number
): { after: number | null; before: number | null } | null => {
  const from = ids.indexOf(draggedId);
  if (from === -1 || slot === from || slot === from + 1) return null;
  const others = ids.filter(id => id !== draggedId);
  const at = slot > from ? slot - 1 : slot;
  return { after: at > 0 ? others[at - 1] : null, before: at < others.length ? others[at] : null };
};

/** The order the group shows after the drop: the dragged task taken out and put into the slot. */
export const reorderedIds = (ids: number[], draggedId: number, slot: number): number[] => {
  const from = ids.indexOf(draggedId);
  if (from === -1) return ids;
  const others = ids.filter(id => id !== draggedId);
  const at = slot > from ? slot - 1 : slot;
  return [...others.slice(0, at), draggedId, ...others.slice(at)];
};

/** A dragged task and the tasks it was dropped between (null at the top or bottom of its group). */
export type ListMove = { taskId: number; after: number | null; before: number | null };

type OrderedTask = { id: number; dueDate?: string | Date | null; listOrder?: number | null };

/**
 * What the server will save for a move (TaskListOrderService, kept the same here so the row moves at once): the first
 * move numbers every task in the list's usual order (no due date first, then by due date, then oldest first), after
 * any numbered already; the moved task takes a number between its neighbours', or right next to the one given. Returns
 * the numbers that change, by task id. The server's answer then replaces them.
 */
export const placeMove = (tasks: OrderedTask[], move: ListMove): Map<number, number> => {
  const orders = new Map<number, number>();
  tasks.forEach(task => {
    if (task.listOrder !== null && task.listOrder !== undefined) orders.set(task.id, task.listOrder);
  });
  const changed = new Map<number, number>();
  let next = orders.size ? Math.max(...Array.from(orders.values())) : 0;
  const time = (task: OrderedTask) => (task.dueDate ? new Date(task.dueDate).getTime() : -Infinity);
  tasks
    .filter(task => !orders.has(task.id))
    .sort((a, b) => time(a) - time(b) || a.id - b.id)
    .forEach(task => {
      next += 1;
      orders.set(task.id, next);
      changed.set(task.id, next);
    });
  const after = move.after === move.taskId ? null : move.after;
  const before = move.before === move.taskId ? null : move.before;
  if (after === null && before === null) return changed;
  const others = Array.from(orders.entries())
    .filter(([id]) => id !== move.taskId)
    .map(([, order]) => order)
    .sort((a, b) => a - b);
  let low: number;
  let high: number;
  if (after !== null && before !== null) {
    low = orders.get(after) ?? 0;
    high = orders.get(before) ?? low + 2;
  } else if (after !== null) {
    low = orders.get(after) ?? 0;
    high = others.find(order => order > low) ?? low + 2;
  } else {
    high = orders.get(before!) ?? 0;
    const below = others.filter(order => order < high);
    low = below.length ? below[below.length - 1] : high - 2;
  }
  // Crowded or the wrong way round: the server renumbers; its answer puts the rows right.
  if (high - low > 1e-6) changed.set(move.taskId, (low + high) / 2);
  return changed;
};
